import { useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { consumeAccessToken } from '@/services/api'
import { saveProcessId, saveWebReturnUrl } from '@/services/session'
import { advanceToStep } from '@/services/navigation'
import { usePageLoader } from '@/components'
import { useStartSignProcess } from './useStartSignProcess'
import { useNavigationGuard } from '@/services/navigation-guard'

/**
 * HASH token flow hook
 *
 * Handles the complete HASH token consumption workflow:
 * - Consumes access token via API
 * - Saves process ID and web return URL
 * - Starts the signing process
 * - Advances navigation steps to QR code
 * - Manages loading and error states
 *
 * @returns Object with processing function, status, and error handling
 */
export interface HashTokenFlow {
  processHashToken: (token: string) => Promise<void>
  isProcessing: boolean
  error: string | null
  clearError: () => void
}

export function useHashTokenFlow(): HashTokenFlow {
  const { t } = useTranslation()
  const { navigateTo } = useNavigationGuard()

  const { showPageLoader, hidePageLoader } = usePageLoader()
  const { startSign } = useStartSignProcess()

  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const processHashToken = async (token: string): Promise<void> => {
    setIsProcessing(true)
    setError(null)
    showPageLoader()

    await consumeAccessToken(token, {
      onSuccess: async response => {
        try {
          // Save process metadata from token consumption
          saveProcessId(response.id)
          if (response.webReturnUrl) {
            saveWebReturnUrl(response.webReturnUrl)
          }

          // Start the signing process
          await startSign({
            noPesel: false,
            email: undefined,
            marketingConsents: undefined,
          })

          // Advance navigation steps to QR code
          advanceToStep('add-document')
          advanceToStep('qr-code')
          navigateTo('/qr-code')

          setIsProcessing(false)
          hidePageLoader()
        } catch (startSignError) {
          hidePageLoader()
          setIsProcessing(false)

          // Handle startSign error
          const errorMessage =
            (
              startSignError as {
                response?: { data?: { detail?: string } }
              }
            )?.response?.data?.detail ?? t('errors.terms.apiFailure')

          setError(errorMessage)
          throw startSignError
        }
      },
      onError: consumeError => {
        hidePageLoader()
        setIsProcessing(false)

        const errorMessage =
          consumeError.response?.data?.detail ?? t('errors.terms.apiFailure')

        setError(errorMessage)
        throw consumeError
      },
    })
  }

  const clearError = () => {
    setError(null)
  }

  return {
    processHashToken,
    isProcessing,
    error,
    clearError,
  }
}
